import React, { useState } from 'react';
import { gql, useMutation } from '@apollo/client';
import { Modal, ModalAction } from '../common/Modal';
import { TextInput } from '../common/TextInput';
import { Select } from '../common/LeboncoinSelect';
import { toast } from 'react-toastify';
import { AddSupplierProductModal_SyncAliexpressSupplierProductMutation, AddSupplierProductModal_SyncAliexpressSupplierProductMutationVariables } from '../../../codegen/graphql';

const CREATE_SUPPLIER_PRODUCT = gql`
  mutation CreateSupplierProduct($input: CreateSupplierProductInput!) {
    createSupplierProduct(input: $input) {
      id
      supplierName
      name
    }
  }
`;

const ADD_SUPPLIER_PRODUCT_MODAL_SYNC_ALIEXPRESS_SUPPLIER_PRODUCT = gql`
  mutation AddSupplierProductModal_SyncAliexpressSupplierProduct(
    $productId: String!,
    $countries: [String!]!,
    $options: SyncAliexpressSupplierProductOptionsInput
  ) {
    syncAliexpressSupplierProduct(
      productId: $productId,
      countries: $countries,
      options: $options
    ) {
      id
      name
      data
    }
  }
`;

const LINK_SUPPLIER_PRODUCT = gql`
  mutation LinkSupplierProduct($adId: ID!, $supplierProductId: String!) {
    linkAdToSupplierProduct(adId: $adId, supplierProductId: $supplierProductId) {
      id
    }
  }
`;



interface AddSupplierProductModalProps {
  onClose: () => void;
  onSuccess: (supplierProduct: any) => void;
  adId: string;
}

export function AddSupplierProductModal({
  onClose,
  onSuccess,
  adId
}: AddSupplierProductModalProps) {
  const [supplierName, setSupplierName] = useState('');
  const [name, setName] = useState('');
  const [aliexpressUrl, setAliexpressUrl] = useState('');

  const [createSupplierProduct, { loading }] = useMutation(CREATE_SUPPLIER_PRODUCT);
  const [syncSupplierProduct] = useMutation<
    AddSupplierProductModal_SyncAliexpressSupplierProductMutation,
    AddSupplierProductModal_SyncAliexpressSupplierProductMutationVariables
  >(
    ADD_SUPPLIER_PRODUCT_MODAL_SYNC_ALIEXPRESS_SUPPLIER_PRODUCT
  );
  const [linkSupplierProduct] = useMutation(LINK_SUPPLIER_PRODUCT);

  const supplierOptions = [
    { identifier: 'FR_ilyes', label: 'FR Ilyes' },
    { identifier: 'aliexpress', label: 'AliExpress' }
  ];

  const isValid = (() => {
    if (!supplierName) {
      return false;
    }

    if (supplierName === 'FR_ilyes') {
      return !!name;
    } else if (supplierName === 'aliexpress') {
      return !!aliexpressUrl;
    }
  })();

  const handleSubmit = async () => {
    if (!isValid) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    if (supplierName === 'FR_ilyes') {
      try {
        const { data } = await createSupplierProduct({
          variables: {
            input: {
              supplierName,
              name
            }
          }
        });

        await linkSupplierProduct({
          variables: {
            adId: adId,
            supplierProductId: data.createSupplierProduct.id,
          }
        })

        toast.success('Produit fournisseur créé avec succès');
        onSuccess(data.createSupplierProduct);
        onClose();
        
        // Reset form
        setSupplierName('');
        setName('');
      } catch (error) {
        console.error('Error creating supplier product:', error);
        toast.error('Erreur lors de la création du produit fournisseur');
      }
    } else if (supplierName === 'aliexpress') {
      try {
        // Extract product ID from URL
        const url = new URL(aliexpressUrl);
        const productId = url.pathname.split('/').pop()?.split('.')[0];
        
        if (!productId) {
          throw new Error('Invalid Aliexpress URL');
        }
  
        // Close modal and import supplier
        onClose();
  
        const { data: productResponse } = await syncSupplierProduct({
          variables: {
            productId,
            countries: ['FR'],
            options: {
              forceRefresh: false,
            },
          },
        });

        if (!productResponse) {
          throw new Error('Failed to sync Aliexpress product, empty response');
        }
  
        const { data: linkResponse } = await linkSupplierProduct({
          variables: {
            adId: adId,
            supplierProductId: productResponse.syncAliexpressSupplierProduct.id,
          },
        });
        toast.success('Fournisseur importé avec succès');
      } catch (error) {
        toast.error('URL Aliexpress invalide. Veuillez vous assurer de coller une URL de produit valide.');
      }
    }
  };

  const modalActions: ModalAction[] = [
    {
      label: 'Annuler',
      onClick: onClose,
      preset: 'white'
    },
    {
      label: supplierName === 'FR_ilyes' ? 'Créer' : 'Importer',
      onClick: handleSubmit,
      preset: 'primary',
      disabled: !isValid || loading
    }
  ];


  const ImportAliexpressModalContent = () => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        URL du produit Aliexpress
      </label>
      <input
        type="text"
        value={aliexpressUrl}
        onChange={(e) => setAliexpressUrl(e.target.value)}
        placeholder="https://www.aliexpress.com/item/..."
        className="w-full px-3 py-2 border border-gray-300 rounded-md"
      />
    </div>
  );
  return (
    <Modal
      title="Ajouter un produit fournisseur"
      onClose={onClose}
      actions={modalActions}
      width="medium"
    >
      <div className="space-y-4">
        <Select
          label="Fournisseur"
          options={supplierOptions}
          value={supplierName}
          onChange={setSupplierName}
          required
        />

        {supplierName === 'FR_ilyes' && (
          <TextInput
            label="Nom du produit"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Entrer le nom du produit"
            required
          />
        )}

        {supplierName === 'aliexpress' && (
          <TextInput
            type="text"
            value={aliexpressUrl}
            onChange={(e) => setAliexpressUrl(e.target.value)}
            placeholder="https://www.aliexpress.com/item/..."
            required
          />
        )}
      </div>
    </Modal>
  );
} 